import {Component, inject, OnInit, signal} from '@angular/core';
import {FooterComponent} from "../../../common/footer/footer.component";
import {FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {HeaderComponent} from "../../../common/header/header.component";

import {LocaleService} from "../../../../locale/locale.service";
import {FormNavigationService} from "../../../../services/form-navigation/form-navigation.service";
import { NavigationKey } from '../../../../enums/navigation-key-type';
import {ClaimsService} from "../../../../services/claims.service";
import {IncidentType} from "../../../../enums/incident-type";
import {VehicleIncidentStatusType} from "../../../../enums/vehicle-incident-status-type";
import {InvolvedVehicleType} from "../../../../enums/involved-vehicle-type";
import {ClaimType} from "../../../../enums/claim-type";

@Component({
  selector: 'app-bodily-injured-accident-description-list',
  imports: [
    FooterComponent,
    FormsModule,
    HeaderComponent,
    ReactiveFormsModule
  ],
  templateUrl: './incident-description.component.html',
  styleUrl: './incident-description.component.css'
})
export class IncidentDescriptionComponent implements OnInit {
  locale = inject(LocaleService);
  private formNavigationService = inject(FormNavigationService);
  private claimService = inject(ClaimsService);
  private fb = inject(FormBuilder);

  showError: boolean = false;
  options: { headline: string, caption: string, value: string }[] = [];
  radioControl = new FormControl();
  isMultipleOptions = signal(false);
  multipleOptionsForm: FormGroup = this.fb.group({
    selectedOptions: this.fb.array([])
  });

  ngOnInit(): void {
    this.claimService.getData().subscribe(() => {
      this.fillOptionValues();
      if (this.claimService?.claimReport()?.incidentTypes) {
        this.radioControl = new FormControl(this.claimService.claimReport().incidentTypes?.[0]);
      }
      if (this.claimService?.claimReport()?.vehicleType === InvolvedVehicleType.None && this.claimService?.claimReport()?.claimType === ClaimType.Both) {
        this.isMultipleOptions.set(true);
        const selectedParts = this.claimService.claimReport().incidentTypes || [];
        const selectedOptions = this.multipleOptionsForm.get('selectedOptions') as FormArray;

        selectedParts.forEach((part: string) => {
          if (this.options.some(x => x.value === part)) {
            selectedOptions.push(new FormControl(part));
          }
        });
      } else {
        this.isMultipleOptions.set(false);
      }
    })
  }

  private fillOptionValues() {
    if (this.claimService?.claimReport()?.vehicleType === InvolvedVehicleType.None) {
      switch (this.claimService?.claimReport()?.claimType) {
        case ClaimType.Both:
          this.options = [
            this.getInjuredPersonOption(),
            this.getInjuryWhileAvoidingOption(),
            this.getPersonalDamagedItemOption(),
            this.getOtherPropertyDamageOption(),
            this.getAnimalInjuryOption(),
          ];
          break;
        case ClaimType.PropertyDamage:
          this.options = [
            this.getPersonalDamagedItemOption(),
            this.getOtherPropertyDamageOption(),
            this.getAnimalInjuryOption(),
          ];
          break;
        case ClaimType.BodilyInjured:
          this.options = [
            this.getInjuredPersonOption(),
            this.getInjuryWhileAvoidingOption(),
          ];
          break;
        default:
          this.options = [];
          break;
      }
    } else {
      if (this.claimService?.claimReport()?.vehicleIncidentStatusType === VehicleIncidentStatusType.InMotion) {
        this.options = [
          this.getFailureToYieldOption(),
          this.getRearEndCollisionOption(),
          this.getOncomingTrafficCollisionOption(),
          this.getSideSwipeOption(),
          this.LaneChangeIncidentOption(),
          this.getParkingIncidentOption(),
          this.getOtherOption(),
        ]
      }
      if (this.claimService?.claimReport()?.vehicleIncidentStatusType === VehicleIncidentStatusType.Parked) {
        this.options = [
          this.getParkedVehicleCollisionOption(),
          this.getParkingIncidentOption(),
          this.getOtherOption(),
        ]
      }
    }
  }

  nextClicked() {
    this.evaluateShowError()
    if (!this.showError) {
      const getSelectedOptions = this.isMultipleOptions() ? this.multipleOptionsForm.get('selectedOptions')?.getRawValue() : [this.radioControl.getRawValue()];
      this.claimService.updateClaimReport({incidentTypes: getSelectedOptions});
      this.claimService.save().subscribe(() => {
        this.formNavigationService.navigate(NavigationKey['incident-description']);
      });
    }
  }

  private evaluateShowError() {
    let rawValue = this.radioControl.getRawValue();
    const selectedOptions = this.multipleOptionsForm.get('selectedOptions')?.getRawValue();
    if (this.isMultipleOptions()) {
      this.showError = selectedOptions.length === 0 || !this.options.some(x => selectedOptions.includes(x.value));
    } else {
      this.showError = rawValue == null || !this.options.some(x => x.value === rawValue);
    }
  }

  setOption(value: string) {
    this.radioControl.patchValue(value);
    this.showError = false;
  }

  selectCheckbox(e: any) {
    const selectedOptions: FormArray = this.multipleOptionsForm.get('selectedOptions') as FormArray;

    if (e.target.checked) {
      selectedOptions.push(this.fb.control(e.target.value));
    } else {
      const index = selectedOptions.controls.findIndex(x => x.value === e.target.value);
      if (index >= -1) {
        selectedOptions.removeAt(index);
      }
    }

    this.showError = false
  }

  isSelected(value: string): boolean {
    const fa = this.multipleOptionsForm.get('selectedOptions') as FormArray;
    return fa.value.includes(value);
  }

  private getParkedVehicleCollisionOption() {
    return {
      headline: this.locale.currentTranslation.incidentDescriptionOptionHeadline2,
      caption: this.locale.currentTranslation.incidentDescriptionOptionCaption2,
      value: IncidentType.ParkedVehicleCollision
    };
  }

  private getOtherOption() {
    return {
      headline: this.locale.currentTranslation.incidentDescriptionOptionHeadline11,
      caption: this.locale.currentTranslation.incidentDescriptionOptionCaption11,
      value: IncidentType.Other
    };
  }

  private getParkingIncidentOption() {
    return {
      headline: this.locale.currentTranslation.incidentDescriptionOptionHeadline8,
      caption: this.locale.currentTranslation.incidentDescriptionOptionCaption8,
      value: IncidentType.ParkingIncident
    };
  }

  private LaneChangeIncidentOption() {
    return {
      headline: this.locale.currentTranslation.incidentDescriptionOptionHeadline7,
      caption: this.locale.currentTranslation.incidentDescriptionOptionCaption7,
      value: IncidentType.LaneChangeIncident
    };
  }

  private getSideSwipeOption() {
    return {
      headline: this.locale.currentTranslation.incidentDescriptionOptionHeadline6,
      caption: this.locale.currentTranslation.incidentDescriptionOptionCaption6,
      value: IncidentType.SideSwipe
    };
  }

  private getOncomingTrafficCollisionOption() {
    return {
      headline: this.locale.currentTranslation.incidentDescriptionOptionHeadline5,
      caption: this.locale.currentTranslation.incidentDescriptionOptionCaption5,
      value: IncidentType.OncomingTrafficCollision
    };
  }

  private getRearEndCollisionOption() {
    return {
      headline: this.locale.currentTranslation.incidentDescriptionOptionHeadline4,
      caption: this.locale.currentTranslation.incidentDescriptionOptionCaption4,
      value: IncidentType.RearEndCollision
    };
  }

  private getFailureToYieldOption() {
    return {
      headline: this.locale.currentTranslation.incidentDescriptionOptionHeadline1,
      caption: this.locale.currentTranslation.incidentDescriptionOptionCaption1,
      value: IncidentType.FailureToYield
    };
  }

  private getInjuryWhileAvoidingOption() {
    return {
      headline: this.locale.currentTranslation.incidentDescriptionOptionHeadline16,
      caption: this.locale.currentTranslation.incidentDescriptionOptionCaption16,
      value: IncidentType.InjuryWhileAvoiding
    };
  }

  private getInjuredPersonOption() {
    return {
      headline: this.locale.currentTranslation.incidentDescriptionOptionHeadline15,
      caption: this.locale.currentTranslation.incidentDescriptionOptionCaption15,
      value: IncidentType.InjuredPerson
    };
  }

  private getAnimalInjuryOption() {
    return {
      headline: this.locale.currentTranslation.incidentDescriptionOptionHeadline14,
      caption: this.locale.currentTranslation.incidentDescriptionOptionCaption14,
      value: IncidentType.AnimalInjury
    };
  }

  private getOtherPropertyDamageOption() {
    return {
      headline: this.locale.currentTranslation.incidentDescriptionOptionHeadline13,
      caption: this.locale.currentTranslation.incidentDescriptionOptionCaption13,
      value: IncidentType.OtherPropertyDamage
    };
  }

  private getPersonalDamagedItemOption() {
    return {
      headline: this.locale.currentTranslation.incidentDescriptionOptionHeadline12,
      caption: this.locale.currentTranslation.incidentDescriptionOptionCaption12,
      value: IncidentType.PersonalDamagedItem
    };
  }
}
